import { bootstrapSession } from '@/shared/api/apolloClient';
import { useAuthStore } from '@/stores/authStore';
import { useWorkstationStore } from '@/stores/workstationStore';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4000/graphql';
const REST_BASE = API_URL.replace(/\/graphql\/?$/, '');

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().access_token;
  const ws = useWorkstationStore.getState().workstation_id;
  const h: Record<string, string> = {};
  if (token) h.Authorization = `Bearer ${token}`;
  if (ws) h['X-Workstation-Id'] = ws;
  return h;
}

/** Mirrors `attachments-rest.ts` — same silent-refresh-on-401 retry. */
async function authedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const attempt = (): Promise<Response> =>
    fetch(url, { ...init, credentials: 'include', headers: { ...authHeaders(), ...(init.headers ?? {}) } });
  let res = await attempt();
  if (res.status === 401 && (await bootstrapSession())) {
    res = await attempt();
  }
  return res;
}

export interface VoiceNoteAnamnesisSuggestion {
  chief_complaint?: string;
  symptom_onset_days?: number;
  previous_illnesses?: string;
  current_medications?: string;
  vaccination_status?: string;
  deworming_status?: string;
  diet_notes?: string;
  environment_notes?: string;
  other_notes?: string;
  surgeries_notes?: string;
  last_heat_notes?: string;
  barf_diet_notes?: string;
  fleas_ticks_notes?: string;
  cohabitation_notes?: string;
  street_exposure_notes?: string;
}

export interface VoiceNotePhysicalExamSuggestion {
  body_weight_grams?: number;
  body_temperature_celsius_tenths?: number;
  heart_rate_bpm?: number;
  respiratory_rate_bpm?: number;
  body_condition_score?: number;
  mucous_membranes?: string;
  hydration_status?: string;
  consciousness_level?: string;
  cardiovascular_notes?: string;
  respiratory_notes?: string;
  gastrointestinal_notes?: string;
  genitourinary_notes?: string;
  neurological_notes?: string;
  integumentary_notes?: string;
  lymph_node_notes?: string;
  abdomen_notes?: string;
  musculoskeletal_notes?: string;
  general_impression?: string;
  capillary_refill_time?: string;
  cough_reflex?: string;
  gag_reflex?: string;
  pulse_description?: string;
  auscultation_percussion_notes?: string;
}

export interface VoiceNoteDiagnosisSuggestion {
  description: string;
  diagnosis_kind: string;
  is_primary: boolean;
}

export interface VoiceNotePlanSuggestion {
  treatment_notes?: string;
  medications_notes?: string;
  diet_recommendations?: string;
  activity_restrictions?: string;
  follow_up_notes?: string;
  owner_instructions?: string;
}

export interface VoiceNoteSuggestions {
  anamnesis: VoiceNoteAnamnesisSuggestion;
  physical_exam: VoiceNotePhysicalExamSuggestion;
  /** Keys in `physical_exam` filled from the species "normal" reference table (the vet said
   *  something like "todo lo demás normal") rather than dictated explicitly. */
  physical_exam_inferred_fields: string[];
  diagnoses: VoiceNoteDiagnosisSuggestion[];
  plan: VoiceNotePlanSuggestion;
}

export interface TranscribeVoiceNoteResult {
  id: string;
  status: 'processing' | 'succeeded' | 'partial' | 'failed' | 'dead';
  language: string | null;
  transcript_text: string | null;
  suggestions: VoiceNoteSuggestions;
  audio_seconds: number | null;
  error_message: string | null;
  skald_request_id: string | null;
}

/** Uploads the dictation + runs the Skald pipeline job in one round trip. */
export async function transcribeVoiceNote(
  visitId: string,
  blob: Blob,
  filename: string,
): Promise<TranscribeVoiceNoteResult> {
  const fd = new FormData();
  fd.append('file', blob, filename);
  const res = await authedFetch(`${REST_BASE}/clinical/voice-notes/${visitId}/transcribe`, {
    method: 'POST',
    body: fd,
  });
  if (!res.ok) {
    if (res.status === 413) throw new Error('clinical_voice_note_too_large');
    let code = 'clinical_voice_note_transcribe_failed';
    try {
      const j = (await res.json()) as { message?: string };
      if (typeof j.message === 'string') code = j.message;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(code);
  }
  return (await res.json()) as TranscribeVoiceNoteResult;
}
